import { LEGACY_EACH_DENOMINATOR, UNKNOWN_UNIT, isNamedForm } from "@/constants/medication-dosage";

import { fhirR4 } from "@smile-cdr/fhirts";
import { MedicationStatement } from "@smile-cdr/fhirts/dist/FHIR-R4/classes/medicationStatement";

import { type DosageStep, dosageToSteps, emptyDosageStep, stepsToDosage } from "./dosage-steps";
import { FhirResourceType, formatters } from "./fhir-utils";
import {
  CONTAINED_MEDICATION_ID,
  type IngredientRow,
  buildContainedMedication,
  emptyIngredientRow,
  formValueFromLabel,
  getContainedMedication,
  medicationToIngredientRows,
  resolveMedicationName,
} from "./medication-ingredients";

// Extension URL constants (from engage-frontend-v2)
export const EXTENSION_URL = "https://vitall.com/documentation/extensions";
export const VITALL_SYSTEM = "https://vitall.com/documentation";

// Legacy dose/frequency extension URLs. These are no longer written — dose,
// frequency and timing now live in the structured FHIR `dosage[]` (VMD-581) —
// but we still strip them on update to clean up resources created earlier.
const LEGACY_MEDICATIONS_DOSE = "medications/dose";
const LEGACY_MEDICATIONS_FREQUENCY = "medications/frequency";

// Contraindication extension constants
export const CONTRAINDICATION_EXTENSION = "contraindication";
export const CONTRAINDICATION_REASON = "reason";

// Status values for MedicationStatement (from FHIR R4)
export type MedicationStatementStatus =
  "active" | "completed" | "entered-in-error" | "intended" | "stopped" | "on-hold" | "unknown" | "not-taken";

// Status map for display (from engage-frontend-v2)
export const MEDICATION_STATUS_MAP: Record<MedicationStatementStatus, string> = {
  active: "Active",
  intended: "Intended",
  unknown: "Unknown",
  completed: "Completed",
  "entered-in-error": "Entered In Error",
  stopped: "Stopped",
  "on-hold": "On Hold",
  "not-taken": "Not Taken",
};

// Category map for display (from engage-frontend-v2)
export const MEDICATION_CATEGORY_MAP: Record<string, string> = {
  prescription: "Prescription",
  overthecounter: "Over the Counter",
};

// Therapeutic drug classes based on Vitall coding system
export const MEDICATION_CLASS_MAP: Record<string, string> = {
  acei: "ACEi",
  arbs: "ARBs",
  arnis: "ARNIs",
  betaBlockers: "Beta Blockers",
  mras: "MRAs",
  sglt2i: "SGLT2i",
  loopDiuretics: "Loop Diuretics",
  thiazides: "Thiazides",
  nitrates: "Nitrates",
  cardiacGlycosides: "Cardiac Glycosides",
  ifChannelBlockers: "IF-Channel Blockers",
  sgcStimulators: "SGC Stimulators",
  directVasodilators: "Direct Vasodilators",
  biguanides: "Biguanides",
  glp1s: "GLP-1s",
  dpp4i: "DPP-4i",
  insulins: "Insulins",
  sulfonylureas: "Sulfonylureas",
  thiazolidinediones: "Thiazolidinediones",
  doacs: "DOACs",
  p2y12Inhibitors: "P2Y12 Inhibitors",
  salicylates: "Salicylates",
  statins: "Statins",
  cholesterolAbsorptionInhibitors: "Cholesterol Absorption Inhibitors",
  pcsk9i: "PCSK9i",
  vkas: "VKAs",
  classIIIAntiarrhythmics: "Class III Antiarrhythmics",
  ironSupplements: "Iron Supplements",
  esas: "ESAs",
  potassiumBinders: "Potassium Binders",
  pde5i: "PDE5i",
  eras: "ERAs",
  prostacyclinAnalogues: "Prostacyclin Analogues",
  thyroidReplacements: "Thyroid Replacements",
  antithyroidAgents: "Antithyroid Agents",
  xanthineOxidaseInhibitors: "Xanthine Oxidase Inhibitors",
  colchicineAlkaloids: "Colchicine Alkaloids",
  steroids: "Steroids",
  labas: "LABAs",
  lamas: "LAMAs",
  cbs: "CBs",
};

// Form data interface for the medications table
export interface MedicationFormData {
  id?: string;
  name: string;
  /**
   * The dose form, chosen once and the single source of the noun in "Each
   * tablet contains 3 mg" and "Takes 1 to 2 tablets". Optional: blank means the
   * source never named a form, and the quantity then stores `unit: "unknown"`.
   */
  form: string;
  /** Active-ingredient strengths; more than one makes it a combination product. */
  ingredients: IngredientRow[];
  dosageSteps: DosageStep[];
  route: string;
  medicationClass: string; // "prescription" | "overthecounter" | custom class values
  status: MedicationStatementStatus;
  isContraindication: boolean;
  reason?: string; // For contraindication reason (stored in extension)
}

/**
 * The form always opens on at least one strength row, so "Add combination dose"
 * reads as adding the second. An untouched row has no content, so it neither
 * validates nor saves.
 */
const withAtLeastOneRow = (rows: IngredientRow[]): IngredientRow[] => (rows.length > 0 ? rows : [emptyIngredientRow()]);

/**
 * Creates an empty medication form data object
 */
export const emptyMedicationState = (isContraindication = false): MedicationFormData => ({
  id: undefined,
  name: "",
  form: "",
  ingredients: [emptyIngredientRow()],
  dosageSteps: [emptyDosageStep()],
  route: "",
  medicationClass: "",
  status: "active",
  isContraindication,
  reason: "",
});

/**
 * Check if a MedicationStatement is a contraindication
 * Checks for the contraindication extension on the resource
 */
export const isContraindication = (resource: MedicationStatement): boolean => {
  const contraindicationExt = resource.extension?.find(
    ext => ext.url === `${EXTENSION_URL}/${CONTRAINDICATION_EXTENSION}`
  );
  return !!contraindicationExt;
};

/**
 * Convert a FHIR MedicationStatement to form data
 */
export const medicationStatementToFormData = (resource: MedicationStatement): MedicationFormData => {
  // Dose / frequency / timing now live in the structured dosage[].
  const dosageSteps = dosageToSteps(resource.dosage);

  // Get route from dosage
  const route = resource.dosage?.[0]?.route?.text ?? "";

  // Get category/class from category coding
  const medicationClass = resource.category?.coding?.[0]?.code ?? "";

  // Get medication name (contained Medication first, legacy CodeableConcept last)
  const name = resolveMedicationName(resource);

  // Combination products carry one ingredient row per active strength
  const ingredients = withAtLeastOneRow(medicationToIngredientRows(resource));

  // Shared with Engage, which owns this order. The dose unit is what the record
  // says the patient takes, so it wins; then an authored denominator, skipping
  // the synthesised `each` nobody chose; then the product's own form.
  //
  // Deliberately unfiltered. A legacy record whose only unit is a mass seeds
  // `form: "mg"`, which `unitOptionsFor` keeps selectable and the editor flags
  // as not a dose form. Filtering it to blank would leave a required field empty
  // on exactly the records that have least to say.
  const storedDose = resource.dosage?.[0]?.doseAndRate?.[0];
  const authoredDenominator = ingredients.map(row => row.per).find(per => per && per !== LEGACY_EACH_DENOMINATOR);
  const form =
    storedDose?.doseQuantity?.unit ||
    storedDose?.doseRange?.low?.unit ||
    storedDose?.doseRange?.high?.unit ||
    authoredDenominator ||
    formValueFromLabel(getContainedMedication(resource)?.form?.text) ||
    "";

  // Get status
  const status = (resource.status as MedicationStatementStatus) ?? "unknown";

  // Check if it's a contraindication
  const isContraIndicationResource = isContraindication(resource);

  // Get reason from contraindication extension
  const contraindicationExt = resource.extension?.find(
    ext => ext.url === `${EXTENSION_URL}/${CONTRAINDICATION_EXTENSION}`
  );
  const reason = contraindicationExt?.valueCodeableConcept?.text ?? "";

  return {
    id: resource.id,
    name,
    form,
    ingredients,
    dosageSteps,
    route,
    medicationClass,
    status,
    isContraindication: isContraIndicationResource,
    reason,
  };
};

/**
 * Applies the chosen form to the dosage steps.
 *
 * Called when the curator picks a Form, not on every save. Picking is an
 * explicit act, so it overwrites: a legacy "10 mg" dose or a "4000 mg" ceiling
 * takes the new noun, and so does every ingredient denominator. An unchanged
 * form has to be a no-op through the write path, or an untouched open-and-save
 * rewrites units nobody touched.
 *
 * A quantity with no form stores the `unknown` literal, which is what the rules
 * ask for on a bare number. The ceiling takes a named form only: §3 bars
 * `unknown` there.
 */
export const applyFormToSteps = (steps: DosageStep[], form: string): DosageStep[] =>
  steps.map(step => ({
    ...step,
    doseUnit: step.doseValue ? form || UNKNOWN_UNIT : "",
    maxDoseUnit: step.maxDoseValue && isNamedForm(form) ? form : "",
  }));

/**
 * The form data as it will actually be written.
 *
 * Reconciling only when the form differs from the one the record hydrated with
 * is what makes an untouched open-and-save a no-op: leave the Form alone and
 * every stored unit survives. Change it and it overwrites, because picking a
 * Form is an explicit act — a legacy "10 mg" dose, a "4000 mg" ceiling and every
 * ingredient denominator take the new noun.
 *
 * Gated here rather than in the select's onChange so that a value typed *after*
 * the pick still gets the noun. The preview runs through the same call, so what
 * is on screen is what gets saved.
 */
export const projectForm = (data: MedicationFormData, initialForm: string): MedicationFormData =>
  data.form === initialForm
    ? data
    : {
        ...data,
        dosageSteps: applyFormToSteps(data.dosageSteps, data.form),
        ingredients: data.ingredients.map(row => ({ ...row, per: data.form })),
      };

/**
 * Points a statement's medication at a contained `Medication` built from the
 * form, replacing whatever medication[x] the resource carried before. Every
 * record uses this one model — single-ingredient and combination alike.
 */
const applyMedicationReference = (resource: MedicationStatement, data: MedicationFormData): void => {
  // The form edits names and strengths but never codings, so whatever the
  // resource already carries is passed in to have its coding carried across.
  // A legacy record holds that coding on `medicationCodeableConcept` and has no
  // contained Medication — the proxy returns those untouched rather than
  // synthesizing one (VMD-703) — so this is the only chance to preserve it.
  const previous =
    getContainedMedication(resource) ??
    (resource.medicationCodeableConcept
      ? ({ resourceType: "Medication", code: resource.medicationCodeableConcept } satisfies fhirR4.Medication)
      : undefined);

  const medication = buildContainedMedication(
    data.name,
    data.ingredients,
    data.form,
    previous,
    // The name the record resolved to before this edit — comparing against the
    // raw stored text drops codings on concepts that carry no text of their own.
    resolveMedicationName(resource)
  );

  // Replace only the one contained resource we own — the Medication at our own
  // id. Anything else stays, including a Medication under a different id, which
  // some other element may hold a local reference to.
  const otherContained = (resource.contained ?? []).filter(
    entry => !(entry?.resourceType === "Medication" && entry?.id === CONTAINED_MEDICATION_ID)
  );
  resource.contained = [...otherContained, medication];

  resource.medicationReference = {
    reference: `#${CONTAINED_MEDICATION_ID}`,
    type: FhirResourceType.Medication,
    // An empty display would defeat the proxy's name resolution for old clients.
    ...(data.name ? { display: data.name } : {}),
  };

  // medication[x] is a choice type — the CodeableConcept form must not survive
  // alongside the reference (the read proxy re-injects it for older clients).
  delete resource.medicationCodeableConcept;
};

/**
 * Convert form data to a FHIR MedicationStatement resource
 */
export const formDataToMedicationStatement = (data: MedicationFormData, patientId: string): MedicationStatement => {
  const extensions: fhirR4.Extension[] = [];

  const medicationStatement: MedicationStatement = {
    resourceType: FhirResourceType.MedicationStatement,
    subject: {
      reference: formatters.formatPatient(patientId),
      type: FhirResourceType.Patient,
    },
    status: data.status,
  };

  applyMedicationReference(medicationStatement, projectForm(data, ""));

  // Add ID if updating existing resource
  if (data.id) {
    medicationStatement.id = data.id;
  }

  // Add category if provided
  if (data.medicationClass) {
    const displayText = MEDICATION_CLASS_MAP[data.medicationClass] ?? data.medicationClass;
    medicationStatement.category = {
      coding: [
        {
          system: VITALL_SYSTEM,
          code: data.medicationClass,
          display: displayText,
        },
      ],
      text: displayText,
    };
  }

  // Dose / frequency / timing / route → structured dosage[].
  const dosage = stepsToDosage(projectForm(data, "").dosageSteps, { route: data.route });
  if (dosage.length > 0) {
    medicationStatement.dosage = dosage;
  }

  // Add contraindication extension if applicable
  if (data.isContraindication) {
    extensions.push({
      url: `${EXTENSION_URL}/${CONTRAINDICATION_EXTENSION}`,
      valueCodeableConcept: {
        coding: [
          {
            system: VITALL_SYSTEM,
            code: "contraindicated",
            display: "Contraindicated",
          },
        ],
        text: data.reason || "Contraindication",
      },
    });
  }

  // Add extensions if any
  if (extensions.length > 0) {
    medicationStatement.extension = extensions;
  }

  return medicationStatement;
};

/**
 * Update an existing MedicationStatement with form data
 */
export const updateMedicationStatementFromFormData = (
  existingResource: MedicationStatement,
  data: MedicationFormData
): MedicationStatement => {
  // Create a deep copy to avoid modifying the original
  const updated: MedicationStatement = JSON.parse(JSON.stringify(existingResource));

  // Ensure subject reference includes type field
  if (updated.subject?.reference && !updated.subject.type) {
    updated.subject.type = "Patient";
  }

  // Reconciled once, and only if the Form actually moved. Everything below reads
  // the projection so the ingredients and the dosage cannot disagree about it.
  const projected = projectForm(data, medicationStatementToFormData(existingResource).form);

  // Update medication name / ingredients via the contained Medication
  applyMedicationReference(updated, projected);

  // Update status
  updated.status = data.status;

  // Update category
  if (data.medicationClass) {
    const displayText = MEDICATION_CLASS_MAP[data.medicationClass] ?? data.medicationClass;
    updated.category = {
      coding: [
        {
          system: VITALL_SYSTEM,
          code: data.medicationClass,
          display: displayText,
        },
      ],
      text: displayText,
    };
  } else {
    delete updated.category;
  }

  // Dose / frequency / timing / route → structured dosage[], merged onto what
  // is already stored so fields the form cannot represent (site, method,
  // asNeededCodeableConcept, repeat.dayOfWeek, …) survive the edit.
  const dosage = stepsToDosage(projected.dosageSteps, { route: data.route, existing: existingResource.dosage });
  if (dosage.length > 0) {
    updated.dosage = dosage;
  } else {
    delete updated.dosage;
  }

  // Keep unrelated extensions; drop legacy dose/frequency extensions and the
  // contraindication extension (which we rebuild below).
  const existingOtherExtensions = (updated.extension ?? []).filter(
    ext =>
      !ext.url?.includes(LEGACY_MEDICATIONS_DOSE) &&
      !ext.url?.includes(LEGACY_MEDICATIONS_FREQUENCY) &&
      ext.url !== `${EXTENSION_URL}/${CONTRAINDICATION_EXTENSION}`
  );

  // Build contraindication extension if applicable
  const newExtensions: fhirR4.Extension[] = [];
  if (data.isContraindication) {
    newExtensions.push({
      url: `${EXTENSION_URL}/${CONTRAINDICATION_EXTENSION}`,
      valueCodeableConcept: {
        coding: [
          {
            system: VITALL_SYSTEM,
            code: "contraindicated",
            display: "Contraindicated",
          },
        ],
        text: data.reason || "Contraindication",
      },
    });
  }

  // Combine all extensions
  const allExtensions = [...existingOtherExtensions, ...newExtensions];
  if (allExtensions.length > 0) {
    updated.extension = allExtensions;
  } else {
    delete updated.extension;
  }

  return updated;
};

/**
 * Get status category for filtering (active vs inactive)
 */
export const getStatusCategory = (status: MedicationStatementStatus): "active" | "inactive" | "unknown" => {
  switch (status) {
    case "active":
    case "intended":
      return "active";
    case "unknown":
      return "unknown";
    case "completed":
    case "entered-in-error":
    case "stopped":
    case "on-hold":
    case "not-taken":
      return "inactive";
    default:
      return "inactive";
  }
};

/**
 * Get all available status values
 */
export const getAllMedicationStatuses = (): MedicationStatementStatus[] => {
  return Object.keys(MEDICATION_STATUS_MAP) as MedicationStatementStatus[];
};

// Status GROUP order for the All view (VMD-687): Active first, On Hold second, then
// every remaining status alphabetically by its display label.
const PINNED_STATUS_GROUP_ORDER: MedicationStatementStatus[] = ["active", "on-hold"];

/**
 * Returns every status in group-display order: the pinned statuses (Active, On Hold)
 * first, then the rest sorted alphabetically by their display label.
 */
export const getOrderedMedicationStatuses = (): MedicationStatementStatus[] => {
  const remaining = getAllMedicationStatuses()
    .filter(status => !PINNED_STATUS_GROUP_ORDER.includes(status))
    .sort((a, b) => MEDICATION_STATUS_MAP[a].localeCompare(MEDICATION_STATUS_MAP[b]));
  return [...PINNED_STATUS_GROUP_ORDER, ...remaining];
};

export const extractMedNameFromMedStatement = (resource: MedicationStatement) => resolveMedicationName(resource);

/**
 * Get all available medication classes
 */
export const getAllMedicationClasses = (): string[] => {
  return Object.keys(MEDICATION_CLASS_MAP);
};
